import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { getVisitorId, setVisitorId, trackVisit } from "../utils/analytics";
import ChunkyDatePicker from "../components/ChunkyDatePicker";

const BASE = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { setName, setDob, hydrateFromServer, dob: existingDob } = useStore();
  const [msg, setMsg] = useState("Signing you in…");
  const [needDob, setNeedDob] = useState(false);
  const [dobValue, setDobValue] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const doneRef = useRef(false);

  const sessionId = useMemo(() => {
    const m = location.hash?.match(/session_id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.hash]);

  const todayIso = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (!sessionId) {
      nav("/welcome", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/auth/visitor/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            visitor_id: getVisitorId(),
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          setMsg(data.detail || "Sign-in failed.");
          setTimeout(() => nav("/welcome", { replace: true }), 1500);
          return;
        }
        // Server may have folded us into an older canonical linked visitor —
        // rotate localStorage so subsequent analytics use the canonical id.
        if (data.visitor_id) setVisitorId(data.visitor_id);
        // Hydrate the persistent selections (avatar/name/dob) from the
        // canonical row so a device switch or auto-merge doesn't leave the
        // display showing the local default 🐻.
        let serverProfile = {};
        try {
          const meRes = await fetch(
            `${BASE}/api/visitor/me?visitor_id=${encodeURIComponent(data.visitor_id || getVisitorId())}`,
            { credentials: "include" },
          );
          if (meRes.ok) {
            serverProfile = await meRes.json();
            hydrateFromServer(serverProfile);
          }
        } catch { /* best effort */ }
        // Only fall back to the Google session's name/dob when the canonical
        // row doesn't already have them — the canonical row is the source of
        // truth once a user has customised their profile.
        const displayName =
          serverProfile.last_name ||
          data.display_name ||
          (data.name ? data.name.split(" ")[0] : "");
        if (displayName && !serverProfile.last_name) setName(displayName);
        setDisplayName(displayName);
        const effectiveDob = serverProfile.dob || data.dob || existingDob || "";
        if (data.dob && !serverProfile.dob) setDob(data.dob);
        // Prompt for DOB only when neither the canonical row, the OAuth
        // session, nor local state has one yet.
        if (!effectiveDob) {
          setNeedDob(true);
          setMsg(`Hi ${displayName || "friend"}! One quick thing…`);
        } else {
          nav("/shop", { replace: true });
        }
      } catch {
        setMsg("Network error. Redirecting…");
        setTimeout(() => nav("/welcome", { replace: true }), 1500);
      }
    })();
  }, [sessionId, nav, setName, setDob, existingDob, hydrateFromServer]);

  const saveDob = async (e) => {
    e?.preventDefault?.();
    if (savingDob) return;
    setSavingDob(true);
    if (dobValue) {
      setDob(dobValue);
      try {
        await trackVisit(displayName || "", dobValue, undefined, { profileUpdate: true });
      } catch { /* analytics best-effort */ }
    }
    nav("/shop", { replace: true });
  };

  const skipDob = () => {
    if (savingDob) return;
    nav("/shop", { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center dots-bg px-4"
      data-testid="auth-callback"
    >
      <div
        className="chunky-card p-6 text-center w-full max-w-sm"
        style={{ background: "#fff" }}
      >
        <div className="text-5xl bob mb-2" aria-hidden="true">☕</div>
        <div className="font-display text-xl" data-testid="auth-callback-msg">
          {msg}
        </div>

        {needDob && (
          <form onSubmit={saveDob} className="mt-4 flex flex-col gap-3 text-left">
            <label
              className="text-xs font-display"
              style={{ color: "#484848" }}
            >
              Birthday (optional — all ages welcome)
            </label>
            <ChunkyDatePicker
              value={dobValue}
              onChange={setDobValue}
              testIdPrefix="auth-dob"
              maxYear={new Date().getFullYear()}
            />
            <input
              type="hidden"
              data-testid="auth-dob-input"
              value={dobValue}
              readOnly
            />
            <div className="flex gap-2">
              <button
                type="submit"
                data-testid="auth-dob-save"
                className="btn-3d flex-1"
                disabled={savingDob}
                style={{ fontSize: 16 }}
              >
                Save &amp; play
              </button>
              <button
                type="button"
                onClick={skipDob}
                data-testid="auth-dob-skip"
                className="btn-3d btn-ghost flex-1"
                disabled={savingDob}
                style={{ fontSize: 16 }}
              >
                Skip
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
